import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Plus, X, Search, PackageSearch, PenLine, Check, Loader2, AlertCircle } from 'lucide-react';
import { inventoryAPI } from '../../services/api';
import { cn } from '../../lib/utils';

// ─── Product Search Dropdown ───────────────────────────────────────────────────
const ProductSearchDropdown = ({ onSelect, onManualMode }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [searched, setSearched] = useState(false);
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const search = useCallback(async (q) => {
        if (!q || q.trim().length < 1) {
            setResults([]);
            setOpen(false);
            setSearched(false);
            return;
        }
        setLoading(true);
        setOpen(true);
        try {
            const res = await inventoryAPI.searchProducts(q.trim());
            const data = res.data?.content || res.data || [];
            setResults(data);
            setSearched(true);
        } catch {
            setResults([]);
            setSearched(true);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => search(val), 350);
    };

    const handleSelect = (product) => {
        onSelect(product);
        setOpen(false);
        setQuery('');
        setResults([]);
    };

    const handleManual = () => {
        onManualMode(query);
        setOpen(false);
        setQuery('');
    };

    return (
        <div ref={wrapperRef} className="relative">
            {/* Search Input */}
            <div className={cn(
                "flex items-center gap-2 rounded-xl border-2 bg-white dark:bg-slate-800/40 px-3 py-2 transition-all",
                open ? "border-[#FF7D00] ring-4 ring-[#FF7D00]/10 shadow-md" : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700"
            )}>
                {loading
                    ? <Loader2 className="h-4 w-4 text-sky-500 animate-spin shrink-0" />
                    : <Search className="h-4 w-4 text-slate-400 shrink-0" />
                }
                <input
                    ref={inputRef}
                    value={query}
                    onChange={handleChange}
                    onFocus={() => query && setOpen(true)}
                    placeholder="Search inventory by name…"
                    className="flex-1 bg-transparent text-sm font-medium text-slate-700 dark:text-slate-100 outline-none placeholder:text-slate-400"
                    autoComplete="off"
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => { setQuery(''); setResults([]); setOpen(false); setSearched(false); }}
                        className="text-slate-400 hover:text-slate-600"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>

            {/* Dropdown */}
            {open && (
                <div className="absolute z-50 mt-2 w-full min-w-[300px] rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-300/40 overflow-hidden">
                    {/* Results */}
                    {results.length > 0 ? (
                        <ul className="max-h-[220px] overflow-y-auto">
                            {results.map((product) => (
                                <li key={product.productId}>
                                    <button
                                        type="button"
                                        onClick={() => handleSelect(product)}
                                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FF7D00]/10 transition-colors text-left group"
                                    >
                                        <div className="h-8 w-8 rounded-lg bg-orange-50 dark:bg-slate-800 flex items-center justify-center shrink-0 group-hover:bg-[#FF7D00]/20 transition-colors">
                                            <PackageSearch className="h-4 w-4 text-[#FF7D00]" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">{product.productName}</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                {product.brand && <span className="mr-2">{product.brand}</span>}
                                                {product.sellingPriceInr != null &&
                                                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">₹{Number(product.sellingPriceInr).toLocaleString('en-IN')}</span>
                                                }
                                                {product.stockQty != null &&
                                                    <span className="ml-2 text-slate-400">• Stock: {product.stockQty}</span>
                                                }
                                            </p>
                                        </div>
                                        <Check className="h-4 w-4 text-[#FF7D00] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : searched && !loading ? (
                        <div className="px-4 py-3">
                            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                                <AlertCircle className="h-4 w-4 text-amber-500" />
                                <span className="font-semibold">No product found for "<span className="text-slate-700">{query}</span>"</span>
                            </div>
                            <p className="text-xs text-slate-400 ml-6">You can add it as a manual entry below.</p>
                        </div>
                    ) : null}

                    {/* Always show: Manual Entry Option */}
                    {searched && (
                        <div className="border-t border-slate-100 p-2">
                            <button
                                type="button"
                                onClick={handleManual}
                                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors text-left group border border-amber-200"
                            >
                                <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 group-hover:bg-amber-200 transition-colors">
                                    <PenLine className="h-4 w-4 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-amber-800">Add Manual Entry</p>
                                    <p className="text-xs text-amber-600">
                                        {query ? <>Use "<span className="font-semibold">{query}</span>" as product name</> : 'Type a custom product name'}
                                    </p>
                                </div>
                            </button>
                        </div>
                    )}

                    {!searched && !loading && query && (
                        <div className="px-4 py-3 text-xs text-slate-400 text-center">
                            Searching inventory…
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Item Row ──────────────────────────────────────────────────────────────────
const ItemRow = React.memo(({
    index,
    item,
    onUpdateItem,
    onRemoveItem,
    errors = {}
}) => {
    const [localQty, setLocalQty] = useState(item.quantity || 1);
    const [localPrice, setLocalPrice] = useState(item.price || 0);
    const [localPurchasePrice, setLocalPurchasePrice] = useState(item.purchasePrice || 0);
    const [localName, setLocalName] = useState(item.productName || '');
    const [localDesc, setLocalDesc] = useState(item.customDescription || '');

    // Sync when parent changes
    useEffect(() => { setLocalQty(item.quantity ?? 1); }, [item.quantity]);
    useEffect(() => { setLocalPrice(item.price ?? 0); }, [item.price]);
    useEffect(() => { setLocalPurchasePrice(item.purchasePrice ?? 0); }, [item.purchasePrice]);
    useEffect(() => { setLocalName(item.productName ?? ''); }, [item.productName]);
    useEffect(() => { setLocalDesc(item.customDescription ?? ''); }, [item.customDescription]);

    // Debounce push to parent
    const debounce = (fn, ms = 200) => {
        let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const pushQty = useCallback(debounce((v) => onUpdateItem(index, 'quantity', parseInt(v) || 0), 200), [index, onUpdateItem]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const pushPrice = useCallback(debounce((v) => onUpdateItem(index, 'price', parseFloat(v) || 0), 200), [index, onUpdateItem]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const pushPurchasePrice = useCallback(debounce((v) => onUpdateItem(index, 'purchasePrice', parseFloat(v) || 0), 200), [index, onUpdateItem]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const pushName = useCallback(debounce((v) => onUpdateItem(index, 'productName', v), 200), [index, onUpdateItem]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const pushDesc = useCallback(debounce((v) => onUpdateItem(index, 'customDescription', v), 200), [index, onUpdateItem]);

    const lineTotal = (parseFloat(localPrice) || 0) * (parseInt(localQty) || 0) * (1 + (item.gstPercent || 0) / 100);
    const isFromInventory = !!item.productId;
    const isManual = item._isManual;

    /* ── Product Name / Tag area ─────────────────────────── */
    const handleClearProduct = () => {
        onUpdateItem(index, 'productId', '');
        onUpdateItem(index, 'productName', '');
        onUpdateItem(index, 'price', 0);
        onUpdateItem(index, 'purchasePrice', 0);
        onUpdateItem(index, 'gstPercent', 0);
        onUpdateItem(index, '_isManual', false);
    };

    return (
        <Card className={cn(
            "relative overflow-hidden border-2 transition-all duration-300 shadow-sm dark:bg-slate-900/40",
            isFromInventory
                ? "border-orange-200 dark:border-orange-500/20 bg-orange-50/10 hover:border-[#FF7D00]"
                : isManual
                    ? "border-amber-200 dark:border-amber-500/20 bg-amber-50/10 hover:border-amber-400"
                    : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 hover:border-slate-300 dark:hover:border-slate-700"
        )}>
            {/* Type badge */}
            <div className="absolute top-3 right-10">
                {isFromInventory && (
                    <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200">
                        <PackageSearch className="h-3 w-3" /> Inventory
                    </span>
                )}
                {isManual && !isFromInventory && (
                    <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                        <PenLine className="h-3 w-3" /> Manual
                    </span>
                )}
            </div>

            {/* Remove button */}
            <button
                type="button"
                onClick={() => onRemoveItem(index)}
                className="absolute top-3 right-3 h-6 w-6 flex items-center justify-center rounded-full bg-slate-100 hover:bg-rose-100 hover:text-rose-600 text-slate-400 transition-all"
            >
                <X className="h-3.5 w-3.5" />
            </button>

            <CardContent className="p-4 md:p-5 space-y-3">
                {/* Row 1: Product Name Area */}
                <div className="space-y-1.5 pr-24">
                    <Label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                        Product / Item <span className="text-rose-500">*</span>
                    </Label>

                    {/* If product is resolved (inventory or manual confirmed) */}
                    {(isFromInventory || isManual) ? (
                        <div className="flex items-center gap-2">
                            <div className={cn(
                                "flex-1 flex items-center gap-2 rounded-xl border px-3 py-2",
                                isFromInventory ? "bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/20" : "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20"
                            )}>
                                <span className={cn("h-2 w-2 rounded-full shrink-0", isFromInventory ? "bg-[#FF7D00]" : "bg-amber-500")} />
                                <input
                                    value={localName}
                                    onChange={(e) => { setLocalName(e.target.value); pushName(e.target.value); }}
                                    className={cn(
                                        "flex-1 bg-transparent text-sm font-bold outline-none",
                                        isFromInventory ? "text-orange-800 dark:text-orange-400" : "text-amber-800 dark:text-amber-400"
                                    )}
                                    placeholder="Product name"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={handleClearProduct}
                                title="Change product"
                                className="shrink-0 h-9 w-9 flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400 hover:border-rose-200 hover:text-rose-500 hover:bg-rose-50 transition-all shadow-sm"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    ) : (
                        /* Search mode */
                        <ProductSearchDropdown
                            onSelect={(product) => {
                                onUpdateItem(index, 'productId', product.productId);
                                onUpdateItem(index, 'productName', product.productName);
                                onUpdateItem(index, 'price', product.sellingPriceInr || product.purchasePriceInr || 0);
                                onUpdateItem(index, 'purchasePrice', product.purchasePriceInr || 0);
                                onUpdateItem(index, 'gstPercent', product.gstPercent || 0);
                                onUpdateItem(index, '_isManual', false);
                            }}
                            onManualMode={(typedName) => {
                                onUpdateItem(index, '_isManual', true);
                                onUpdateItem(index, 'productId', '');
                                onUpdateItem(index, 'productName', typedName || '');
                            }}
                        />
                    )}

                    {errors[`items_${index}_productName`] && (
                        <p className="text-[10px] font-black text-rose-600 uppercase tracking-tight">
                            {errors[`items_${index}_productName`]}
                        </p>
                    )}
                </div>

                {/* Row 2: Qty | Unit Price | GST | Line Total */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
                    {/* Qty */}
                    <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Qty <span className="text-rose-500">*</span></Label>
                        <Input
                            type="number"
                            min="1"
                            value={localQty}
                            onChange={(e) => { setLocalQty(e.target.value); pushQty(e.target.value); }}
                            className={cn(
                                "jit-input h-10 text-center",
                                errors[`items_${index}_quantity`] && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake"
                            )}
                        />
                        {errors[`items_${index}_quantity`] && (
                            <p className="text-[10px] font-black text-red-600 ml-1 mt-1 uppercase">Required</p>
                        )}
                    </div>

                    {/* Selling Price */}
                    <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Unit Price (₹)</Label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">₹</span>
                            <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={localPrice}
                                onChange={(e) => { setLocalPrice(e.target.value); pushPrice(e.target.value); }}
                                className="h-10 pl-6 font-bold"
                            />
                        </div>
                    </div>

                    {/* GST % */}
                    <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">GST %</Label>
                        <div className="relative">
                            <Input
                                type="number"
                                min="0"
                                max="100"
                                value={item.gstPercent ?? 0}
                                onChange={(e) => onUpdateItem(index, 'gstPercent', parseFloat(e.target.value) || 0)}
                                className="h-10 font-bold text-center pr-7"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">%</span>
                        </div>
                    </div>

                    {/* Line Total */}
                    <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total (Inc. GST)</Label>
                        <div className="h-10 flex items-center justify-end font-black text-slate-800 dark:text-slate-100 bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-sm shadow-inner">
                            ₹{lineTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                    </div>
                </div>

                {/* Row 3: Purchase Price (internal) + Description */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-1 border-t border-slate-100/80">
                    <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Purchase Price (₹) <span className="normal-case text-slate-300 font-normal">internal</span></Label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs">₹</span>
                            <Input
                                type="number"
                                min="0"
                                value={localPurchasePrice}
                                onChange={(e) => { setLocalPurchasePrice(e.target.value); pushPurchasePrice(e.target.value); }}
                                className="h-9 pl-6 text-xs bg-slate-50 text-slate-500"
                            />
                        </div>
                    </div>
                    <div className="md:col-span-3 space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Custom Description / Notes</Label>
                        <Input
                            value={localDesc}
                            onChange={(e) => { setLocalDesc(e.target.value); pushDesc(e.target.value); }}
                            placeholder="Additional details for this line item…"
                            className="h-9 text-xs bg-slate-50"
                        />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
});

// ─── Main Form ─────────────────────────────────────────────────────────────────
const QuotationItemForm = ({ items, onAddItem, onUpdateItem, onRemoveItem, errors = {} }) => {
    const grandTotal = items.reduce((sum, item) => {
        const price = parseFloat(item.price) || 0;
        const qty = parseInt(item.quantity) || 0;
        const gst = parseFloat(item.gstPercent) || 0;
        return sum + price * qty * (1 + gst / 100);
    }, 0);

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-amber-500/5 pointer-events-none" />
                <div className="relative flex flex-col gap-0.5">
                    <h4 className="text-lg font-black text-slate-800 dark:text-white tracking-tight flex items-center gap-2">
                        <PackageSearch className="h-5 w-5 text-[#FF7D00]" />
                        Quotation Items
                    </h4>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                        Search inventory to auto-fill details, or add a manual entry
                    </p>
                </div>
                <Button
                    type="button"
                    onClick={onAddItem}
                    size="sm"
                    className="relative shrink-0 bg-[#FF7D00] hover:brightness-110 shadow-md transition-all font-bold text-white px-6"
                >
                    <Plus className="mr-1.5 h-4 w-4" />
                    Add Item
                </Button>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 px-1">
                <div className="flex items-center gap-1.5 text-xs text-[#FF7D00] font-semibold">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#FF7D00]"></span>
                    From Inventory
                </div>
                <div className="flex items-center gap-1.5 text-xs text-amber-500 font-semibold">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-500"></span>
                    Manual Entry
                </div>
            </div>

            {/* Items */}
            <div className="space-y-3">
                {items.length === 0 ? (
                    <div
                        className="text-center py-12 border-2 border-dashed rounded-2xl border-slate-200 cursor-pointer group hover:border-sky-300 hover:bg-sky-50/30 transition-all"
                        onClick={onAddItem}
                    >
                        <div className="flex flex-col items-center gap-3 text-slate-400 group-hover:text-sky-500 transition-colors">
                            <PackageSearch className="h-10 w-10 opacity-40 group-hover:opacity-70" />
                            <div>
                                <p className="font-bold text-sm">No items yet</p>
                                <p className="text-xs mt-0.5">Click here or "Add Item" to begin building your quotation</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    items.map((item, index) => (
                        <ItemRow
                            key={index}
                            index={index}
                            item={item}
                            onUpdateItem={onUpdateItem}
                            onRemoveItem={onRemoveItem}
                            errors={errors}
                        />
                    ))
                )}
            </div>

            {/* Add more button */}
            {items.length > 0 && (
                <Button
                    type="button"
                    onClick={onAddItem}
                    variant="outline"
                    size="sm"
                    className="w-full border-dashed border-2 border-[#FF7D00]/20 text-[#FF7D00] hover:bg-orange-50 dark:hover:bg-slate-900 font-bold rounded-xl h-12"
                >
                    <Plus className="mr-1 h-4 w-4" />
                    Add Another Item
                </Button>
            )}

            {/* Grand Total bar */}
            {items.length > 0 && (
                <div className="flex items-center justify-between bg-[#FF7D00] text-white rounded-2xl px-6 py-4 shadow-lg shadow-orange-500/20">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest opacity-90">Grand Total</p>
                        <p className="text-xs opacity-80 mt-0.5">{items.length} item{items.length !== 1 ? 's' : ''} · Incl. GST</p>
                    </div>
                    <p className="text-3xl font-black tracking-tighter">
                        ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                </div>
            )}
        </div>
    );
};

export default React.memo(QuotationItemForm);
